import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { ReportSection } from '@accounting/shared';
import { useIncomeStatement } from '../../hooks/useReports';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import type { DrillTarget } from '../../components/reports/DrilldownModal';
import { ExportButtons } from '../../components/reports/ExportButtons';
import { DateRangePicker } from '../../components/reports/PeriodFilter';
import { ComparePicker } from '../../components/reports/ComparePicker';
import { RefreshButton } from '../../components/reports/RefreshButton';
import { ActiveOnlyToggle, useActiveOnly } from '../../components/reports/ActiveOnlyToggle';
import { formatAccountingDate, formatCurrency, formatPctOfTotal, todayIso } from '../../lib/formatters';
import { collectParentIds, computeParentRanges } from '../../lib/reportTree';
import {
  computeComparePeriod,
  formatMoneyDelta,
  formatPctChange,
  type ComparePreset,
} from '../../lib/comparePeriod';

export const Route = createFileRoute('/_auth/reports/income-statement')({
  component: IncomeStatementPage,
});

const yearStart = () => `${new Date().getFullYear()}-01-01`;
const lineCodes = (l: { code: string; codes?: string[] }) =>
  l.codes ?? (l.code ? [l.code] : []);
const codesOf = (s: ReportSection) => s.lines.flatMap(lineCodes);

function IncomeStatementPage() {
  const selection = useActiveSelection();
  const navigate = useNavigate();
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(todayIso());
  const [activeOnly, setActiveOnly] = useActiveOnly();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [comparePreset, setComparePreset] = useState<ComparePreset>('none');
  const { data, isLoading, error, refetch, isFetching } = useIncomeStatement(selection, from, to, activeOnly);
  const comparePeriod = useMemo(
    () => computeComparePeriod(comparePreset, { from, to }),
    [comparePreset, from, to],
  );
  // Fire compare query only when a preset is active. Empty strings are skipped
  // by the hook so the noop case doesn't hit the network.
  const { data: compareData } = useIncomeStatement(
    selection,
    comparePeriod?.from ?? '',
    comparePeriod?.to ?? '',
    activeOnly,
  );
  const compareOn = !!comparePeriod && !!compareData;

  const openDrilldown = (target: DrillTarget): void =>
    void navigate({
      to: '/reports/drilldown',
      search: {
        codes: target.codes.join(','),
        label: target.label,
        from,
        to,
        back: '/reports/income-statement',
      },
    });

  const toggleCollapsed = (accountId: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });

  const allParentIds = useMemo(() => {
    if (!data) return [] as string[];
    const ids: string[] = [];
    for (const sec of [data.revenue, data.cogs, data.expenses, data.otherIncome, data.otherExpense]) {
      ids.push(...collectParentIds(sec.lines));
    }
    return ids;
  }, [data]);

  // Index the compare data by accountId for O(1) per-line lookup.
  const compareById = useMemo(() => {
    const m = new Map<string, string>();
    if (!compareData) return m;
    for (const sec of [compareData.revenue, compareData.cogs, compareData.expenses, compareData.otherIncome, compareData.otherExpense]) {
      for (const l of sec.lines) {
        if (l.accountId) m.set(l.accountId, l.amount);
      }
    }
    return m;
  }, [compareData]);

  /** Lookup compare amount for a subtotal line by depth+name within the corresponding compare section. */
  function findCompareSubtotal(line: ReportSection['lines'][number], compareSec: ReportSection | undefined): string | undefined {
    if (!line.isSubtotal || !compareSec) return undefined;
    const match = compareSec.lines.find(
      (cl) => cl.isSubtotal && (cl.depth ?? 0) === (line.depth ?? 0) && cl.name === line.name,
    );
    return match?.amount;
  }

  if (!selection) return <NoCompany />;

  const allCodes = data
    ? [
        ...codesOf(data.revenue),
        ...codesOf(data.cogs),
        ...codesOf(data.expenses),
        ...codesOf(data.otherIncome),
        ...codesOf(data.otherExpense),
      ]
    : [];

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Income Statement
          {selection === CONSOLIDATED && <span className="tag" style={{ marginLeft: 8 }}>Consolidated</span>}
        </h1>
        <div style={{ fontSize: 13, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {allParentIds.length > 0 && (
            <>
              <button
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => setCollapsed(new Set(allParentIds))}
                title="Hide every parent's children; show only the rolled-up totals"
              >
                Collapse all
              </button>
              <button
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => setCollapsed(new Set())}
                title="Show every parent's children"
              >
                Expand all
              </button>
            </>
          )}
          <DateRangePicker from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
          <ActiveOnlyToggle value={activeOnly} onChange={setActiveOnly} />
          <ComparePicker value={comparePreset} onChange={setComparePreset} mode="range" />
          <RefreshButton onRefresh={() => void refetch()} isFetching={isFetching} />
          <ExportButtons selection={selection} type="income_statement" params={activeOnly ? { from, to, activeOnly: 'true' } : { from, to }} />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {data && (
        <div className="card" style={{ padding: 20 }}>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            {formatAccountingDate(data.from)} – {formatAccountingDate(data.to)}
          </p>

          {/* Anchor for the % column = total revenue. Every line on the report is
              shown as a % of revenue (the classic vertical-analysis convention). */}
          {(() => {
            const anchor = data.revenue.total;
            const compareAnchor = compareData?.revenue.total;
            return (
              <>
                {compareOn && comparePeriod && (
                  <CompareHeader currentLabel={`${formatAccountingDate(data.from)} – ${formatAccountingDate(data.to)}`} compareLabel={comparePeriod.label} />
                )}
                <SectionBlock section={data.revenue} compareSection={compareData?.revenue} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={anchor} compareAnchor={compareAnchor} compareById={compareById} findCompareSubtotal={findCompareSubtotal} compareOn={compareOn} />
                <SectionBlock section={data.cogs} compareSection={compareData?.cogs} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={anchor} compareAnchor={compareAnchor} compareById={compareById} findCompareSubtotal={findCompareSubtotal} compareOn={compareOn} />
                <SubtotalRow
                  label="Gross Profit"
                  amount={data.grossProfit}
                  pct={formatPctOfTotal(data.grossProfit, anchor)}
                  compareAmount={compareData?.grossProfit}
                  comparePct={compareAnchor !== undefined && compareData ? formatPctOfTotal(compareData.grossProfit, compareAnchor) : ''}
                  compareOn={compareOn}
                  onDrill={() =>
                    openDrilldown({ codes: [...codesOf(data.revenue), ...codesOf(data.cogs)], label: 'Gross Profit' })
                  }
                />
                <SectionBlock section={data.expenses} compareSection={compareData?.expenses} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={anchor} compareAnchor={compareAnchor} compareById={compareById} findCompareSubtotal={findCompareSubtotal} compareOn={compareOn} />
                <SubtotalRow
                  label="Operating Income"
                  amount={data.operatingIncome}
                  pct={formatPctOfTotal(data.operatingIncome, anchor)}
                  compareAmount={compareData?.operatingIncome}
                  comparePct={compareAnchor !== undefined && compareData ? formatPctOfTotal(compareData.operatingIncome, compareAnchor) : ''}
                  compareOn={compareOn}
                  onDrill={() =>
                    openDrilldown({
                      codes: [...codesOf(data.revenue), ...codesOf(data.cogs), ...codesOf(data.expenses)],
                      label: 'Operating Income',
                    })
                  }
                />
                {data.otherIncome.lines.length > 0 && <SectionBlock section={data.otherIncome} compareSection={compareData?.otherIncome} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={anchor} compareAnchor={compareAnchor} compareById={compareById} findCompareSubtotal={findCompareSubtotal} compareOn={compareOn} />}
                {data.otherExpense.lines.length > 0 && <SectionBlock section={data.otherExpense} compareSection={compareData?.otherExpense} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={anchor} compareAnchor={compareAnchor} compareById={compareById} findCompareSubtotal={findCompareSubtotal} compareOn={compareOn} />}
              </>
            );
          })()}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '2px solid var(--border)',
              marginTop: 12,
              paddingTop: 10,
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            <span>Net Income</span>
            <AmountPctCell
              amount={data.netIncome}
              pct={formatPctOfTotal(data.netIncome, data.revenue.total)}
              bold
              fontSize={16}
              compareAmount={compareData?.netIncome}
              comparePct={
                compareData ? formatPctOfTotal(compareData.netIncome, compareData.revenue.total) : ''
              }
              compareOn={compareOn}
              onClick={() => openDrilldown({ codes: allCodes, label: 'Net Income' })}
            />
          </div>
        </div>
      )}

    </div>
  );
}

/** Right-aligned cell carrying current amount + % of total, optionally a compare amount + delta. */
function AmountPctCell({
  amount,
  pct,
  bold,
  fontSize,
  blankAmount,
  compareAmount,
  comparePct,
  compareOn,
  onClick,
}: {
  amount: string;
  pct: string;
  bold?: boolean;
  fontSize?: number;
  /** When true, render the amount blank instead of "$0.00" — for header rows with no direct postings. */
  blankAmount?: boolean;
  compareAmount?: string;
  comparePct?: string;
  compareOn?: boolean;
  onClick?: () => void;
}) {
  const fw = bold ? 600 : undefined;
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
      {blankAmount ? (
        <span style={{ fontWeight: fw, fontSize, minWidth: 0 }} />
      ) : onClick ? (
        <button className="amount-link" style={{ fontWeight: fw, fontSize }} onClick={onClick}>
          {formatCurrency(amount, true)}
        </button>
      ) : (
        <span style={{ fontWeight: fw, fontSize }}>{formatCurrency(amount, true)}</span>
      )}
      <span className="muted" style={{ minWidth: 56, textAlign: 'right', fontSize: 12 }}>
        {blankAmount ? '' : pct}
      </span>
      {compareOn && (
        <>
          <span style={{ minWidth: 100, textAlign: 'right', fontWeight: fw, fontSize, color: 'var(--text-muted)' }}>
            {compareAmount !== undefined ? formatCurrency(compareAmount, true) : '—'}
          </span>
          <span className="muted" style={{ minWidth: 56, textAlign: 'right', fontSize: 12 }}>
            {comparePct ?? ''}
          </span>
          <span style={{ minWidth: 110, textAlign: 'right', fontSize: 12 }}>
            {compareAmount !== undefined ? formatMoneyDelta(amount, compareAmount) : ''}
          </span>
          <span style={{ minWidth: 64, textAlign: 'right', fontSize: 12 }}>
            {compareAmount !== undefined ? formatPctChange(amount, compareAmount) : ''}
          </span>
        </>
      )}
    </div>
  );
}

/** Column-header row shown above sections when compare is on. */
function CompareHeader({ currentLabel, compareLabel }: { currentLabel: string; compareLabel: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        paddingBottom: 4,
        marginBottom: 6,
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span />
      <div style={{ display: 'flex', gap: 12 }}>
        <span style={{ minWidth: 100, textAlign: 'right' }}>{currentLabel}</span>
        <span style={{ minWidth: 56, textAlign: 'right' }}>%</span>
        <span style={{ minWidth: 100, textAlign: 'right' }}>{compareLabel}</span>
        <span style={{ minWidth: 56, textAlign: 'right' }}>%</span>
        <span style={{ minWidth: 110, textAlign: 'right' }}>$ Δ</span>
        <span style={{ minWidth: 64, textAlign: 'right' }}>% Δ</span>
      </div>
    </div>
  );
}

function SectionBlock({
  section,
  compareSection,
  onDrill,
  collapsed,
  onToggle,
  anchor,
  compareAnchor,
  compareById,
  findCompareSubtotal,
  compareOn,
}: {
  section: ReportSection;
  compareSection?: ReportSection;
  onDrill: (t: DrillTarget) => void;
  collapsed: Set<string>;
  onToggle: (accountId: string) => void;
  anchor: string;
  compareAnchor?: string;
  compareById: Map<string, string>;
  findCompareSubtotal: (line: ReportSection['lines'][number], compareSec: ReportSection | undefined) => string | undefined;
  compareOn: boolean;
}) {
  const sectionCodes = section.lines.flatMap(lineCodes);
  const parents = useMemo(() => computeParentRanges(section.lines), [section.lines]);

  const rows: React.ReactNode[] = [];
  let skipUntil = -1;
  section.lines.forEach((l, i) => {
    if (i <= skipUntil) return;
    const indent = (l.depth ?? 0) * 16;

    if (l.isSubtotal) {
      const cmp = findCompareSubtotal(l, compareSection);
      rows.push(
        <div key={`sub-${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontWeight: 600 }}>
          <span style={{ paddingLeft: indent + 14 }}>Total {l.name}</span>
          <AmountPctCell
            amount={l.amount}
            pct={formatPctOfTotal(l.amount, anchor)}
            bold
            blankAmount={l.hasActivity === false}
            compareAmount={cmp}
            comparePct={cmp !== undefined && compareAnchor ? formatPctOfTotal(cmp, compareAnchor) : ''}
            compareOn={compareOn}
          />
        </div>,
      );
      return;
    }

    const info = parents.get(i);
    const isParent = !!info;
    const isCollapsed = isParent && collapsed.has(l.accountId);
    if (isCollapsed) skipUntil = info!.subtotalIdx;
    const displayAmount = isCollapsed ? info!.rolledUp : l.amount;
    const cmp = l.accountId ? compareById.get(l.accountId) : undefined;
    const codes = lineCodes(l);
    const label = l.code ? `${l.code} — ${l.name}` : l.name;

    const chevron = isParent ? (
      <button
        type="button"
        onClick={() => onToggle(l.accountId)}
        title={isCollapsed ? `Expand ${l.name}` : `Collapse ${l.name}`}
        aria-label={isCollapsed ? `Expand ${l.name}` : `Collapse ${l.name}`}
        className="muted"
        style={{
          background: 'none', border: 0, cursor: 'pointer', padding: 0,
          marginRight: 4, fontSize: 10, lineHeight: 1, width: 10, display: 'inline-block', textAlign: 'center',
        }}
      >
        {isCollapsed ? '▶' : '▼'}
      </button>
    ) : (
      <span style={{ display: 'inline-block', width: 10, marginRight: 4 }} />
    );

    rows.push(
      <div key={l.accountId || `ln-${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
        <span style={{ paddingLeft: indent }}>{chevron}{label}</span>
        <AmountPctCell
          amount={displayAmount}
          pct={formatPctOfTotal(displayAmount, anchor)}
          blankAmount={!isCollapsed && l.hasActivity === false}
          compareAmount={cmp}
          comparePct={cmp !== undefined && compareAnchor ? formatPctOfTotal(cmp, compareAnchor) : ''}
          compareOn={compareOn}
          onClick={codes.length > 0 ? () => onDrill({ codes, label }) : undefined}
        />
      </div>,
    );
  });

  return (
    <div style={{ marginBottom: 14 }}>
      <h2 style={{ fontSize: 15, margin: '0 0 4px', borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
        {section.label}
      </h2>
      {section.lines.length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: '2px 0' }}>None</div>
      )}
      {rows}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, paddingTop: 4 }}>
        <span>Total {section.label}</span>
        <AmountPctCell
          amount={section.total}
          pct={formatPctOfTotal(section.total, anchor)}
          bold
          compareAmount={compareSection?.total}
          comparePct={compareSection && compareAnchor ? formatPctOfTotal(compareSection.total, compareAnchor) : ''}
          compareOn={compareOn}
          onClick={sectionCodes.length > 0 ? () => onDrill({ codes: sectionCodes, label: `Total ${section.label}` }) : undefined}
        />
      </div>
    </div>
  );
}

function SubtotalRow({
  label,
  amount,
  pct,
  compareAmount,
  comparePct,
  compareOn,
  onDrill,
}: {
  label: string;
  amount: string;
  pct: string;
  compareAmount?: string;
  comparePct?: string;
  compareOn?: boolean;
  onDrill: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: 600,
        background: 'var(--bg-surface-2)',
        padding: '6px 8px',
        margin: '4px -8px 14px',
        borderRadius: 6,
      }}
    >
      <span>{label}</span>
      <AmountPctCell
        amount={amount}
        pct={pct}
        bold
        compareAmount={compareAmount}
        comparePct={comparePct}
        compareOn={compareOn}
        onClick={onDrill}
      />
    </div>
  );
}

function NoCompany() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 32 }}>
      <p className="muted">No active company.</p>
      <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
        Go to Companies
      </Link>
    </div>
  );
}
